import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isFalse} from 'ramda-adjunct';

import {
  useGetPreAccountingProcessingSettingsQuery,
  useUpdatePreAccountingProcessingSettingsMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, NullishBoolean, useNavigate, useRequiredParams} from 'shared/web';

type FormValues = {
  branchId: string | null;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
};

const breadcrumbs = [
  {
    label: i18n.t('page.accounting.labels.preProcessingCalculation'),
    href: settingsRoutes.preAccountingProcessingOverview,
  },
];

export function PreAccountingProcessingDetail() {
  const navigate = useNavigate();
  const {id} = useRequiredParams();

  const {
    data: processingItem,
    isLoading: isProcessingItemLoading,
    isError: isProcessingItemError,
  } = useGetPreAccountingProcessingSettingsQuery({id});

  const {
    data: branches,
    branchOptions,
    isLoading: isLoadingBranches,
    isError: isBranchesError,
  } = useBranches();

  const [updateProcessingItem] = useUpdatePreAccountingProcessingSettingsMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await updateProcessingItem({
      id,
      updatePreAccountingProcessingSettingsRequestBody: values,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.preAccountingProcessingOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    branchId: processingItem?.branchId,
    notifyAccountingDocuments: processingItem?.notifyAccountingDocuments ?? false,
    notifyStockReceipts: processingItem?.notifyStockReceipts ?? false,
    notifyStockIssues: processingItem?.notifyStockIssues ?? false,
    notifyStockTransfers: processingItem?.notifyStockTransfers ?? false,
  };

  const isLoading = isProcessingItemLoading || isLoadingBranches;
  const isError = isProcessingItemError || isBranchesError;

  const branchName =
    branches?.branchListItems.find((branch) => branch.id === processingItem?.branchId)
      ?.marketingName ?? i18n.t('page.accounting.labels.tenantDefinition');

  return (
    <>
      <Helmet title={branchName} />
      <SettingsTemplate
        isError={isError}
        isLoading={isLoading}
        header={{
          title: branchName,
          breadcrumbs,
        }}
        data-testid={testIds.settings.preAccountingProcessingDetail('settings-template')}
      >
        <Form<FormValues>
          onSubmit={handleSubmit}
          experimentalZodSchema={getSchema(processingItem?.isSystem)}
          defaultValues={defaultValues}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    isNotClearable
                    options={branchOptions}
                    label={i18n.t('entity.cashRegister.parameters.branch')}
                    isRequired
                    isDisabled={processingItem?.isSystem}
                    data-testid={testIds.settings.preAccountingProcessingDetail('branch')}
                    placeholder={
                      processingItem?.isSystem
                        ? i18n.t('page.accounting.labels.tenantDefinition')
                        : undefined
                    }
                  />
                </Grid>
                <Separator />

                <VStack spacing={4}>
                  <Card variant="inlineGrey" title={i18n.t('entity.accounting.labels.accounting')}>
                    <FormField
                      control={control}
                      type="checkbox"
                      name="notifyAccountingDocuments"
                      label={i18n.t('entity.accounting.labels.notifyAccountingDocuments')}
                      data-testid={testIds.settings.preAccountingProcessingDetail(
                        'notifyAccountingDocuments'
                      )}
                    />
                  </Card>

                  <Card
                    variant="inlineGrey"
                    title={i18n.t('page.accountingSettings.labels.warehouseMovements')}
                  >
                    <VStack spacing={2}>
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockReceipts"
                        label={i18n.t('page.accountingSettings.labels.notifyReceipt')}
                        data-testid={testIds.settings.preAccountingProcessingDetail(
                          'notifyReceipt'
                        )}
                      />
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockIssues"
                        label={i18n.t('page.accountingSettings.labels.notifyIssue')}
                        data-testid={testIds.settings.preAccountingProcessingDetail('notifyIssue')}
                      />
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockTransfers"
                        label={i18n.t('page.accountingSettings.labels.notifyTransfer')}
                        data-testid={testIds.settings.preAccountingProcessingDetail(
                          'notifyTransfer'
                        )}
                      />
                    </VStack>
                  </Card>
                </VStack>
              </SettingsSection>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () =>
                      navigateWithoutBlocker(settingsRoutes.preAccountingProcessingOverview),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const getSchema = (isSystem: NullishBoolean) => {
  const branchIdSchema = isFalse(isSystem) ? z.string().nonempty() : z.string().nullable();

  return z.object({
    branchId: branchIdSchema,
    notifyAccountingDocuments: z.boolean(),
    notifyStockReceipts: z.boolean(),
    notifyStockIssues: z.boolean(),
    notifyStockTransfers: z.boolean(),
  });
};
